import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFeaturedSpaces, getTopRatedSpaces, searchHeavyDutyMachineries } from '../services/HeavyDutyMachineryService';
import { IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import HeavyDutyMachineryCarousel from './HeavyDutyMachineryCarousel';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const NEAR_YOU_RADIUS_KM = 100;
const SHELF_SIZE = 8;

const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Landing page's discovery content, below the search bar -- three public,
 * no-auth shelves (same as /heavy-duty-machinery and the space detail page, see
 * AppRoutes.tsx): Featured this week, Top rated, and Near you. Each shelf
 * hides itself entirely once loaded with zero spaces (see
 * HeavyDutyMachineryCarousel) rather than showing an empty/error state; "Near
 * you" additionally never renders at all if geolocation is denied or
 * unavailable, per the same reasoning.
 */
const DiscoverySpaces: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [featured, setFeatured] = useState<IHeavyDutyMachinery[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [topRated, setTopRated] = useState<IHeavyDutyMachinery[]>([]);
  const [topRatedLoading, setTopRatedLoading] = useState(true);

  const [coords, setCoords] = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [geoAvailable, setGeoAvailable] = useState(true);
  const [nearYou, setNearYou] = useState<IHeavyDutyMachinery[]>([]);
  const [nearYouLoading, setNearYouLoading] = useState(false);

  useEffect(() => {
    getFeaturedSpaces(SHELF_SIZE)
      .then(setFeatured)
      .catch(() => setFeatured([]))
      .finally(() => setFeaturedLoading(false));
  }, []);

  useEffect(() => {
    getTopRatedSpaces(SHELF_SIZE)
      .then(setTopRated)
      .catch(() => setTopRated([]))
      .finally(() => setTopRatedLoading(false));
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoAvailable(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => setCoords({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => setGeoAvailable(false),
      { timeout: 10000 }
    );
  }, []);

  useEffect(() => {
    if (!coords) return;
    setNearYouLoading(true);
    searchHeavyDutyMachineries({ lat: coords.lat, lng: coords.lng, radiusKm: NEAR_YOU_RADIUS_KM, page: 1, pageSize: SHELF_SIZE })
      .then((result) => setNearYou(result.items))
      .catch(() => setNearYou([]))
      .finally(() => setNearYouLoading(false));
  }, [coords]);

  const distanceById: Record<string, number> | undefined = coords
    ? Object.fromEntries(
        nearYou
          .filter((space) => space.latitude != null && space.longitude != null)
          .map((space) => [space.id, haversineKm(coords.lat, coords.lng, space.latitude as number, space.longitude as number)])
      )
    : undefined;

  const goToSpace = (space: IHeavyDutyMachinery) => navigate(`/heavy-duty-machinery/${space.id}`);

  return (
    <div>
      <HeavyDutyMachineryCarousel
        title={translate(t, TRANSLATIONS.HOMEPAGE_DISCOVERY_FEATURED_TITLE)}
        spaces={featured}
        loading={featuredLoading}
        onSelect={goToSpace}
      />
      <HeavyDutyMachineryCarousel
        title={translate(t, TRANSLATIONS.HOMEPAGE_DISCOVERY_TOP_RATED_TITLE)}
        spaces={topRated}
        loading={topRatedLoading}
        onSelect={goToSpace}
      />
      {geoAvailable && (
        <HeavyDutyMachineryCarousel
          title={translate(t, TRANSLATIONS.HOMEPAGE_DISCOVERY_NEAR_YOU_TITLE)}
          spaces={nearYou}
          loading={nearYouLoading}
          onSelect={goToSpace}
          distanceById={distanceById}
        />
      )}
    </div>
  );
};

export default DiscoverySpaces;
